import { pickedApiRequest, pickedApiCandidateRequest } from './request'
import { readDraft, writeDraft, clearDraft, sameResponses, validDraftResponses } from '../utils/assessmentRecovery'

let credential = null
export function setAssessmentCredential(value) { credential = value }

export class AssessmentSession {
  constructor(jobId) {
    this.jobId = jobId
    this.data = null
    this.queue = Promise.resolve()
    this.uncertain = null
    this.conflict = false
    this.disposed = false
  }

  request(path, body) {
    const client = this.jobId ? pickedApiCandidateRequest : pickedApiRequest
    return client.post('assessment/candidate-exam-sessions' + path,
      { ...body, jobId: this.jobId },
      { timeout: 15000, headers: !this.jobId && credential ? { 'X-Assessment-Session': credential } : {} }
    ).then(response => response.data.data)
  }

  async start(exam, extraTime) {
    this.startBody = { examId: exam.uuid, custom: !!exam.organisationId, extraTime: !!extraTime }
    this.data = await this.request('', this.startBody)
    this.recovered = false
    this.recoveryConflict = false
    const draft = readDraft(this.data.sessionId)
    if (this.data.completed || this.expired()) { clearDraft(this.data.sessionId) } else if (draft && validDraftResponses(this.data, draft.responses)) {
      if (draft.deadlineAt === this.data.deadlineAt && draft.revision === this.data.revision) {
        this.recovered = !sameResponses(draft.responses, this.data.responses)
        if (this.recovered) { return { ...this.data, responses: draft.responses } }
      } else if (!sameResponses(draft.responses, this.data.responses)) {
        this.recoveryConflict = true
      }
      clearDraft(this.data.sessionId)
    } else if (draft) { clearDraft(this.data.sessionId) }
    return this.data
  }

  expired() { return this.data.deadlineAt && Date.parse(this.data.deadlineAt) <= Date.parse(this.data.serverNow) }
  remember(responses) { return this.data && !this.disposed && writeDraft(this.data, responses) }
  dispose() { this.disposed = true }

  async reconcile(snapshot, error) {
    if (!error.response || error.response.status !== 409) { throw error }
    const latest = await this.request('', this.startBody)
    // A lost acknowledgement is recoverable only when the exact submitted responses are stored.
    const ownAcknowledgement = latest.revision === this.data.revision + 1 &&
      (sameResponses(latest.responses, snapshot) || (this.uncertain && sameResponses(latest.responses, this.uncertain)))
    if (latest.completed || ownAcknowledgement) {
      this.data = latest
      this.uncertain = null
      return latest
    }
    this.conflict = true
    throw error
  }

  checkpoint(responses) {
    const snapshot = JSON.parse(JSON.stringify(responses))
    const operation = this.queue.then(async () => {
      if (this.disposed) { return this.data }
      if (this.conflict) { throw Object.assign(new Error('Progress changed in another tab.'), { response: { status: 409 } }) }
      let data
      try {
        data = await this.request('/' + this.data.sessionId + '/checkpoint', { revision: this.data.revision, responses: snapshot })
      } catch (error) {
        if (!error.response || error.response.status >= 500) { this.uncertain = snapshot; throw error }
        data = await this.reconcile(snapshot, error)
        if (!data.completed && !this.expired() && !sameResponses(data.responses, snapshot)) {
          data = await this.request('/' + data.sessionId + '/checkpoint', { revision: data.revision, responses: snapshot })
        }
      }
      this.data = { ...this.data, ...data }
      this.uncertain = null
      if (!this.disposed) {
        const draft = readDraft(this.data.sessionId)
        if (draft && !sameResponses(draft.responses, snapshot) && !this.expired()) {
          writeDraft(this.data, draft.responses)
        } else { clearDraft(this.data.sessionId) }
      }
      return this.data
    })
    this.queue = operation.catch(() => {})
    return operation
  }

  async complete(responses, signals) {
    const snapshot = JSON.parse(JSON.stringify(responses))
    await this.queue
    const submit = () => this.request('/' + this.data.sessionId + '/complete', { ...signals, revision: this.data.revision, responses: snapshot })
    let result
    try { result = await submit() } catch (error) {
      await this.reconcile(snapshot, error)
      result = await submit()
    }
    clearDraft(this.data.sessionId)
    return result
  }
}
